import { useState, useEffect, useCallback } from "react"
import { string, array, bool } from "prop-types"
import Icon from "components/icon_implementation_details"
import TransactionsTable from "components/admin/payments_sort/TransactionsTable"
import { Select } from "components/ui"
import { getDateRange } from "components/admin/payments_sort/calculateDateRange"
import { api } from "utils/api"
import { ToastProvider } from "components/ui/Toast"
import useFlipperFlag from "hooks/use_flipper_flag"

const DATE_RANGE_DEFAULT = "this"
const PAYMENT_METHOD_DEFAULT = "all"
const TYPE_DEFAULT = "all"
const PER_PAGE_LIMIT = 25

const Transactions = ({ apiBaseUrl, isAdmin, stripePayoutsUrl, availableYears = [] }) => {
  const urlParams = new URLSearchParams(window.location.search)
  const typeParam = urlParams.get("type") || TYPE_DEFAULT
  const pageParam = urlParams.get("page") || "1"
  const dateRangeParam = urlParams.get("range") || DATE_RANGE_DEFAULT
  const paymentMethodParam = urlParams.get("method") || PAYMENT_METHOD_DEFAULT
  const displayAchFlipperEnabled = useFlipperFlag("ROLLOUT_display_ach_transactions")

  const [isFetching, setIsFetching] = useState(true)
  const [transactions, setTransactions] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [page, setPage] = useState(Number(pageParam))
  const [paymentMethod, setPaymentMethod] = useState(paymentMethodParam)
  const [type, setType] = useState(typeParam)
  const [dateRange, setDateRange] = useState(dateRangeParam)
  const [dates, setDates] = useState(getDateRange(dateRangeParam))

  const transactionParams = ({ includeOffset = true } = {}) =>
    [
      `where[start_date]=${dates.startDate}`,
      `where[end_date]=${dates.endDate}`,
      ...[
        paymentMethod !== PAYMENT_METHOD_DEFAULT &&
          `where[instrument]=${paymentMethod === "credit" ? "credit card" : paymentMethod}`
      ],
      ...[type !== TYPE_DEFAULT && `where[type]=${type}`],
      `order=-created_at`,
      `per_page=${PER_PAGE_LIMIT}`,
      ...[includeOffset && `offset=${(page - 1) * PER_PAGE_LIMIT}`]
    ]
      .filter(p => !!p)
      .join("&")

  const fetchTransactions = useCallback(async () => {
    setIsFetching(true)

    const response = await api.get(`${apiBaseUrl}/transactions?${transactionParams()}`)

    const count = response.meta.total_count
    const pages = Math.ceil(count / PER_PAGE_LIMIT)

    setTransactions(response.data)
    setTotalCount(count)
    setTotalPages(pages)
    setPage(page > pages ? 1 : page)
    setIsFetching(false)
  }, [paymentMethod, dates, page, type])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  const handleUrlChange = ({ paymentMethodValue, dateRangeValue, typeValue, pageValue }) => {
    const params = [
      ...[paymentMethodValue !== PAYMENT_METHOD_DEFAULT && `method=${paymentMethodValue}`],
      ...[dateRangeValue !== DATE_RANGE_DEFAULT && `range=${dateRangeValue}`],
      ...[pageValue !== 1 && `page=${pageValue}`],
      ...[typeValue !== TYPE_DEFAULT && `type=${typeValue}`]
    ].filter(p => !!p)

    history.replaceState(
      history.state,
      "",
      `/payments${params.length > 0 ? "?" : ""}${params.join("&")}`
    )
  }

  const handleDateChange = value => {
    setDateRange(value)
    setDates(getDateRange(value))
    handleUrlChange({
      paymentMethodValue: paymentMethod,
      dateRangeValue: value,
      typeValue: type,
      pageValue: page
    })
  }

  const handlePaymentMethodChange = value => {
    setPaymentMethod(value)
    handleUrlChange({
      paymentMethodValue: value,
      dateRangeValue: dateRange,
      typeValue: type,
      pageValue: page
    })
  }

  const handleTypeChange = value => {
    setType(value)
    handleUrlChange({
      paymentMethodValue: paymentMethod,
      dateRangeValue: dateRange,
      typeValue: value,
      pageValue: page
    })
  }

  const handlePaginationClick = ({ page: value }) => {
    setPage(value)
    handleUrlChange({
      paymentMethodValue: paymentMethod,
      dateRangeValue: dateRange,
      typeValue: type,
      pageValue: value
    })
  }

  const listedPaymentMethods = () => {
    const paymentMethods = [
      { value: "all", label: "All payment methods" },
      { value: "cash", label: "Cash" },
      { value: "check", label: "Check" },
      { value: "credit", label: "Credit card" },
      { value: "other", label: "Other" },
      { value: "scholarship", label: "Scholarship" }
    ]

    if (displayAchFlipperEnabled) {
      paymentMethods.push({ value: "ach", label: "Bank account" })
    }

    return paymentMethods
  }

  return (
    <div>
      <div className="page__header">
        <div className="d-f@sm ai-c jc-sb w-100%">
          <h1 className="d-n d-b@sm mb-0">Payments</h1>
          {isAdmin && (
            <a
              target="_blank"
              rel="noreferrer"
              href={`${stripePayoutsUrl}`}
              className="app-btn btn2"
            >
              <span className="mr-4p p-r t-2p">
                <Icon symbol="general#new-window" />
              </span>
              Stripe payouts
            </a>
          )}
        </div>
      </div>

      <div className="page__content">
        <div
          className="pane"
          css={{
            borderBottom: 0,
            "--pane--border-radius": 0,
            "--pane--box-shadow-color": "transparent"
          }}
        >
          <div className="d-f p-1" css={{ gap: "0.5rem" }}>
            <div>
              <Select
                id="dateRange"
                name="dateRange"
                className="select--small select"
                ariaLabel="Filter by date range"
                value={dateRange}
                onChange={e => handleDateChange(e.target.value)}
                options={[
                  { value: "this", label: "This month" },
                  { value: "last", label: "Last month" },
                  { value: "7days", label: "Last 7 days" },
                  { value: "30days", label: "Last 30 days" },
                  ...availableYears.map(y => ({ value: y, label: y }))
                ]}
              />
            </div>

            <div>
              <Select
                id="paymentMethod"
                name="paymentMethod"
                className="select--small select"
                ariaLabel="Filter by payment method"
                value={paymentMethod}
                onChange={e => handlePaymentMethodChange(e.target.value)}
                options={listedPaymentMethods()}
              />
            </div>

            <div>
              <Select
                id="type"
                name="type"
                className="select--small select"
                ariaLabel="Filter by type"
                value={type}
                onChange={e => handleTypeChange(e.target.value)}
                options={[
                  { value: "all", label: "All types" },
                  { value: "payments", label: "Payments" },
                  { value: "refunds", label: "Refunds" }
                ]}
              />
            </div>
          </div>
        </div>

        <ToastProvider>
          <TransactionsTable
            isFetching={isFetching}
            transactions={transactions}
            totalCount={totalCount}
            totalPages={totalPages}
            page={page}
            onPaginationClick={handlePaginationClick}
            exportParams={transactionParams({ includeOffset: false })}
          />
        </ToastProvider>
      </div>
    </div>
  )
}

Transactions.propTypes = {
  apiBaseUrl: string.isRequired,
  isAdmin: bool.isRequired,
  stripePayoutsUrl: string.isRequired,
  availableYears: array
}

export default Transactions
